import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { Initials } from '#/components/Initials/Initials';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { H4 } from '#/components/Text/H4';
import { H5 } from '#/components/Text/H5';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaJobHit } from '#/modules/search/algolia/types/SearchAlgoliaJobHit';
import type { SearchAlgoliaRecommendedListingHit } from '#/modules/search/algolia/types/SearchAlgoliaRecommendedListingHit';
import { colors } from '#/theme/theme';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { SectionSearchTile } from './SectionSearchTile';
import { TruncatedText } from './TruncatedText';

type SectionProps = {
  result: SearchAlgoliaJobHit | SearchAlgoliaRecommendedListingHit;
  listingType: string;
  carouselLocation?: string;
  fromListingId?: string;
  layout: 'SLIM' | 'REGULAR';
  source?: string;
  additionalAnalyticsProps?: Record<string, string>;
};

export function SearchHitCarouselTile({
  result,
  listingType,
  carouselLocation,
  fromListingId,
  layout,
  source,
  additionalAnalyticsProps,
}: SectionProps) {
  const { locale } = useI18n();
  let location = result.city ?? '';

  if (result.city && result.state) {
    location += ', ';
  }

  if (result.state) {
    location += result.state;
  }

  return (
    <div style={{ margin: '4px auto', paddingInline: '4px' }}>
      <SectionSearchTile
        layout={layout}
        href={
          typeof result.url === 'string'
            ? result.url
            : (result.url?.[locale] ?? '')
        }
        onClick={() => {
          trackClicked('Featured Listing', {
            ...additionalAnalyticsProps,
            listing_type: listingType,
            from_listing_id: fromListingId ?? null,
            to_listing_id: result.objectID,
            carousel_location: carouselLocation ?? null,
            layout,
            source,
          });
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-evenly',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            textAlign: 'left',
            marginBottom: '10px',
          }}
        >
          {layout === 'REGULAR' && (
            <div
              style={{
                height: 120,
                display: 'flex',
                alignItems: 'center',
                marginBottom: 20,
                width: '100%',
                justifyContent: 'center',
              }}
            >
              {result.logo ? (
                <FilestackImage
                  width={240}
                  height={120}
                  expandSize
                  alt={result.name}
                  data={result.logo}
                  noCrop
                />
              ) : (
                <Initials name={result.name} size={120} />
              )}
            </div>
          )}

          <div style={{ width: '100%' }}>
            <H5
              style={{
                width: '100%',
                whiteSpace: 'nowrap',
                color: colors.lightContentGrey,
                lineHeight: 1,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                display: 'block',
                marginBlock: 0,
              }}
            >
              {result.orgName}
            </H5>

            <H4 style={{ marginBlock: '15px', lineHeight: 1.375 }}>
              <TruncatedText>{result.name}</TruncatedText>
            </H4>
          </div>

          <BodyMicro
            style={{
              marginBlock: 0,
              lineHeight: 1,
              color: colors.lightContentGrey,
            }}
          >
            {location}
          </BodyMicro>
        </div>
      </SectionSearchTile>
    </div>
  );
}
